class CartDrawerSection extends HTMLElement {
  cartUpdateUnsubscriber = undefined;

  constructor() {
    super();

    this.cartType = this.dataset.cartType;
    this.drawerClass = "wt-cart__drawer";
    this.drawer = this.querySelector(`.${this.drawerClass}`);
    this.classDrawerActive = `${this.drawerClass}--open`;

    this.pageOverlayClass = "page-overlay-cart";
    this.activeOverlayBodyClass = `${this.pageOverlayClass}-on`;

    this.body = document.body;

    this.triggerQuery = [
      ".wt-cart__trigger",
      ".wt-cart__back-link",
      `.${this.pageOverlayClass}`,
    ].join(", ");

    this.triggers = () =>
      document.querySelectorAll(this.triggerQuery);

    this.isOpen = this.hasAttribute("open");

    this.isCartPage =
      window.location.pathname === window.routes.cart_url;

    this.mainTrigger = document.querySelector(
      ".wt-cart__trigger"
    );

    this.toggleElements = () =>
      this.querySelectorAll(
        this.dataset.toggleTabindex || ""
      );
  }

connectedCallback() {
  this.init();

  if (this.cartType === "page" || this.isCartPage) {
    return;
  }

  this.cartUpdateUnsubscriber = subscribe(
    PUB_SUB_EVENTS.cartUpdate,
    () => {
      this.updateHeaderCartCount();

      if (this.isOpen) {
        setTabindex(this.toggleEelements(), "0");

        const closeButton = this.closeButton();

        if (closeButton) {
          closeButton.focus();
        }
      }
    }
  );
}

  updateHeaderCartCount() {
  fetch(`${routes.cart_url}.js`)
    .then((response) => response.json())
    .then((cart) => {
      const cartCount = document.getElementById("cartCount");

      if (!cartCount) return;

      cartCount.textContent = cart.item_count;

      cartCount.style.display =
        cart.item_count > 0 ? "" : "none";
    })
    .catch((error) => {
      console.error("Unable to update cart count:", error);
    });
}

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  getCloseButton() {
    return this.querySelector(
      ".wt-cart__drawer__close--button"
    );
  }

  getFocusableElements() {
    const selector =
      "button, [href], input, select, [tabindex]";

    const elements = () =>
      Array.from(
        this.querySelectorAll(selector)
      ).filter(
        (el) =>
          !el.hasAttribute("disabled") &&
          el.tabIndex >= 0
      );

    const list = elements();

    return {
      first: list[0],
      last: list[list.length - 1],
    };
  }

  temporaryHideFocusVisible() {
    this.body.classList.add(
      "no-focus-visible"
    );
  }

  openDrawer() {
    this.setAttribute("open", "");
    this.isOpen = true;

    if (this.drawer) {
      this.drawer.classList.add(
        this.classDrawerActive
      );
    }

    this.body.classList.add(
      this.activeOverlayBodyClass
    );

    this.temporaryHideFocusVisible();

    setTabindex(
      this.toggleElements(),
      "0"
    );

    const closeButton =
      this.getCloseButton();

    if (closeButton) {
      closeButton.focus();
    }
  }

  closeDrawer() {
    this.removeAttribute("open");
    this.isOpen = false;

    if (this.drawer) {
      this.drawer.classList.remove(
        this.classDrawerActive
      );
    }

    this.body.classList.remove(
      this.activeOverlayBodyClass
    );

    this.temporaryHideFocusVisible();

    setTabindex(
      this.toggleElements(),
      "-1"
    );

    if (
      this.mainTrigger &&
      typeof this.mainTrigger.focus === "function"
    ) {
      this.mainTrigger.focus();
    }
  }

  toggleDrawer() {
    if (this.isOpen) {
      this.closeDrawer();
    } else {
      this.openDrawer();
    }
  }

  init() {
    /*
     * OPEN / CLOSE TRIGGERS
     */
    this.triggers().forEach((trigger) => {
      trigger.addEventListener(
        "click",
        (event) => {
          event.preventDefault();
          this.toggleDrawer();
        }
      );
    });

    /*
     * CLOSE BUTTON
     *
     * Event delegation is important because
     * CartDrawer HTML gets replaced after cart update.
     */
    this.addEventListener(
      "click",
      (event) => {
        const closeButton =
          event.target.closest(
            ".wt-cart__drawer__close--button"
          );

        if (!closeButton) {
          return;
        }

        event.preventDefault();
        event.stopPropagation();

        this.closeDrawer();
      }
    );

    /*
     * ESC + FOCUS TRAP
     */
    this.addEventListener(
      "keydown",
      (event) => {
        if (
          event.key === "Escape" &&
          this.isOpen
        ) {
          event.preventDefault();
          this.closeDrawer();
          return;
        }

        if (event.key !== "Tab") {
          return;
        }

        const {
          first,
          last,
        } = this.getFocusableElements();

        if (!first || !last) {
          return;
        }

        if (
          event.shiftKey &&
          document.activeElement === first
        ) {
          event.preventDefault();
          last.focus();
        } else if (
          !event.shiftKey &&
          document.activeElement === last
        ) {
          event.preventDefault();
          first.focus();
        }
      }
    );

    /*
     * CART DRAWER REFRESH
     */
    document.addEventListener(
      "cart-drawer:refresh",
      (event) =>
        this.refreshCartDrawer(event)
    );
  }

  renderContents(
    parsedState,
    isClosedCart = true
  ) {
    this.getSectionsToRender().forEach(
      (section) => {
        const sectionElement =
          section.selector
            ? document.querySelector(
                section.selector
              )
            : document.getElementById(
                section.id
              );

        if (!sectionElement) {
          return;
        }

        const html =
          parsedState.sections[
            section.id
          ];

        if (!html) {
          return;
        }

        sectionElement.innerHTML =
          this.getSectionInnerHTML(
            html,
            section.selector
          );
      }
    );

    /*
     * IMPORTANT:
     * Cart HTML has just been replaced.
     * Get fresh drawer reference.
     */
    this.drawer =
      this.querySelector(
        `.${this.drawerClass}`
      );

    /*
     * Product add-to-cart should open drawer.
     */
    if (isClosedCart) {
      setTimeout(() => {
        this.openDrawer();
      }, 50);
    }
  }

  getSectionInnerHTML(
    html,
    selector = ".shopify-section"
  ) {
    const documentHTML =
      new DOMParser().parseFromString(
        html,
        "text/html"
      );

    const element =
      documentHTML.querySelector(
        selector
      );

    return element
      ? element.innerHTML
      : "";
  }

  getSectionsToRender() {
    return [
      {
        id: "cart-drawer",
        selector: "#CartDrawer",
      },
      {
        id: "cart-icon-bubble",
      },
    ];
  }

  refreshCartDrawer() {
    const sections =
      this.getSectionsToRender();

    fetch(
      `${window.Shopify.routes.root}?sections=${sections[0].id},${sections[1].id}`
    )
      .then((response) =>
        response.json()
      )
      .then((response) => {
        this.renderContents(
          {
            sections: response,
          },
          false
        );
      })
      .catch((error) => {
        console.error(
          "Cart drawer refresh error:",
          error
        );
      });
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define(
  "cart-drawer",
  CartDrawerSection
);


class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: "CartDrawer",
        section: "cart-drawer",
        selector: ".drawer__inner",
      },
      {
        id: "cart-icon-bubble",
        section: "cart-icon-bubble",
        selector: ".shopify-section",
      },
    ];
  }
}

customElements.define(
  "cart-drawer-items",
  CartDrawerItems
);